import React, { useState, useEffect } from 'react';
import {
  Snackbar,
  Alert,
  Button,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  SystemUpdate as UpdateIcon
} from '@mui/icons-material';
import { skipWaiting } from '../serviceWorkerRegistration';

/**
 * UpdatePrompt Component
 * Notifies users when a new version of the app is available
 */
const UpdatePrompt = ({ registration }) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);

  useEffect(() => {
    if (!registration) {
      return;
    }

    // Check if there's a waiting service worker
    if (registration.waiting) {
      setWaitingWorker(registration.waiting);
      setShowPrompt(true);
    }

    // Listen for updates
    const handleUpdateFound = () => {
      const installingWorker = registration.installing;
      if (!installingWorker) {
        return;
      }

      installingWorker.addEventListener('statechange', () => {
        if (installingWorker.state === 'installed' && navigator.serviceWorker.controller) {
          setWaitingWorker(installingWorker);
          setShowPrompt(true);
        }
      });
    };

    registration.addEventListener('updatefound', handleUpdateFound);

    return () => {
      registration.removeEventListener('updatefound', handleUpdateFound);
    };
  }, [registration]);

  const handleUpdate = () => {
    if (!waitingWorker) {
      return;
    }

    // Tell the service worker to skip waiting
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });

    // Reload the page when the new service worker takes control
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload();
    });

    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
  };

  if (!showPrompt) {
    return null;
  }

  return (
    <Snackbar
      open={showPrompt}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ bottom: { xs: 70, sm: 24 } }}
    >
      <Alert
        severity="info"
        icon={<UpdateIcon />}
        action={
          <>
            <Button
              color="inherit"
              size="small"
              onClick={handleUpdate}
              variant="outlined"
              sx={{
                borderColor: 'currentColor',
                mr: 1,
                '&:hover': {
                  borderColor: 'currentColor',
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Update Now
            </Button>
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleDismiss}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
        sx={{
          width: '100%',
          maxWidth: 600,
          alignItems: 'center'
        }}
      >
        A new version of PaceEstate is available
      </Alert>
    </Snackbar>
  );
};

export default UpdatePrompt;
